import { gql } from '@apollo/client';
import { Button } from '@ferris/react/components/button';
import { Snackbar } from '@ferris/react/components/snackbar';
import { SnackbarTimeoutSeconds } from '@ferris/react/components/snackbar/Snackbar';
import { TrackingContext } from '@vrbo/react-event-tracking';
import cn from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import { retrieveDataNoCache } from '../../../actions/graphQlSvc';
import { createImpressionEvent, createInteractionEvent, createPageViewEvent } from '../../../utils/EventProviders/EgClickstreamUtil';
import TranslationUtil from '../../../utils/TranslationUtil';
import withPageFlow from '../../PageFlow/PageFlow';
import ResendEmailBtn, { TYPES } from './ResendEmailBtn';
import ErrorBanner from './errorBanner';

export const EMAIL_VERIFICATION_CHECK = gql`
query CheckEmailVerification($userId: String){
    checkEmailVerification(userId: $userId)
}
`;

export const EmailVerification = (props) => {
    const {
        title,
        text,
        helpCTA,
        helpText,
        confirmCTA,
        confirmError,
        resendError,
        referrer,
        joinId,
        userId,
        propertyId,
        sentText
    } = props;

    const [showError, setShowError] = useState(false);
    const [errorText, setErrorText] = useState(confirmError);
    const [isSent, setIsSent] = useState(false);

    const { trigger } = useContext(TrackingContext);

    const resendEmailCallback = async (success) => {
        setIsSent(false);
        setErrorText(resendError);
        setShowError(!success);
        setIsSent(success);
    };

    const onClick = async () => {
        trigger('interaction', createInteractionEvent('email_verification_confirm.submitted', 'email_verification', 'email_is_verified', undefined, '2.3.0'));
        setShowError(false);
        setErrorText(confirmError);
        try {
            const { error: queryError, data } = await retrieveDataNoCache(
                EMAIL_VERIFICATION_CHECK,
                { userId }
            );
            setShowError(!data.checkEmailVerification || queryError);
            if (!queryError && data.checkEmailVerification) {
                const { pageFlow } = props;
                pageFlow.next();
            }
        } catch (e) {
            setShowError(true);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        trigger('pageLoad', { pageName: 'partner-qualifier: email-verification', referrer });
        trigger('pageview', createPageViewEvent('email_verification', '', '2.3.0'));
        trigger('pageLoadGtm', {
            pageName: 'Success',
            event: 'SuccessLoad',
            ApplicationID: joinId
        });
        return () => {
            trigger('pageUnload', { referrer });
        };
    }, []);

    useEffect(() => {
        if (showError) {
            trigger('impression', createImpressionEvent('error_message.presented', 'email_verification', 'email_not_verified'));
        }
    }, [showError]);

    const classes = cn('hotel-onboarding-wrapper', 'ulx-animated');
    const btnClass = cn('btn-full-width');
    return (
        <>
            <ErrorBanner
                hide={!showError}
                text={errorText}
                classNames={['hotel-onboarding-error']}
            />
            <div className={classes}>
                <div className={cn('hotel-onboarding-content')}>
                    <h1>{title}</h1>
                    <p>{text}</p>
                </div>
                {
                    propertyId && (
                        <div className="hotel-onboarding-note">
                            <span>
                                {helpText}
                                {' '}
                                <ResendEmailBtn
                                    type={TYPES.LINK}
                                    text={helpCTA}
                                    callbackFn={resendEmailCallback}
                                    propertyId={propertyId}
                                />
                            </span>
                            <Snackbar
                                classes={cn('hotel-onboarding-snackbar')}
                                visible={isSent}
                                timeout={SnackbarTimeoutSeconds.FOUR}
                            >
                                <span>{sentText}</span>
                            </Snackbar>
                        </div>
                    )
                }
                <Button
                    classes={btnClass}
                    id="confirm-verification-btn"
                    onClick={onClick}
                >
                    {confirmCTA}
                </Button>
            </div>
        </>
    );
};

EmailVerification.propTypes = {
    referrer: PropTypes.string.isRequired,
    joinId: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    helpText: PropTypes.string.isRequired,
    helpCTA: PropTypes.string.isRequired,
    confirmCTA: PropTypes.string.isRequired,
    confirmError: PropTypes.string.isRequired,
    resendError: PropTypes.string.isRequired,
    userId: PropTypes.string.isRequired,
    pageFlow: PropTypes.shape({
        next: PropTypes.func.isRequired
    }).isRequired,
    propertyId: PropTypes.string.isRequired,
    sentText: PropTypes.string.isRequired
};


/* istanbul ignore next */
const mapStateToProps = (state) => {
    const {
        app, lead
    } = state;
    const {
        email, joinId, userId, propertyId
    } = lead;
    const {
        referrer
    } = app;
    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);

    const getTranslation = (suffix, params = {}) => translations.getTranslation(`hotelOnboarding.email.verification.${suffix}`, params);

    return {
        referrer,
        propertyId,
        sentText: translations.getTranslation('emailVerification.error.sentText'),
        title: getTranslation('title'),
        text: getTranslation('text', { email }),
        helpText: getTranslation('resend'),
        helpCTA: getTranslation('resend.cta'),
        confirmCTA: getTranslation('confirm.cta'),
        email,
        joinId,
        confirmError: getTranslation('error.confirm'),
        resendError: getTranslation('error.resend'),
        userId
    };
};

const EmailVerificationCL = connect(mapStateToProps)(EmailVerification);
EmailVerificationCL.displayName = 'ClEmailVerification';
export default hot(module)(withPageFlow(EmailVerificationCL));
